import { useEffect, useState } from 'react';
import { SecurityInsightsClient } from '../../api/SecurityInsightsClient';
import { QuiesceReason, QuiesceReasonCode } from '../../api/types';

/** Active reasons already covering this exact `(team, cve_or_rule)` — the
 * "attach to existing reason" candidates. */
export function useExistingReasons(
  open: boolean,
  trimmedCveOrRule: string,
  team: string | undefined,
  api: SecurityInsightsClient,
): QuiesceReason[] {
  const [existingReasons, setExistingReasons] = useState<QuiesceReason[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !trimmedCveOrRule) {
      setExistingReasons([]);
      return () => {
        cancelled = true;
      };
    }
    api
      .listQuiesceReasons({
        status: 'active',
        selectedCveOrRule: trimmedCveOrRule,
        selectedTeam: team,
      })
      .then(reasons => {
        if (!cancelled) setExistingReasons(reasons);
      })
      .catch(() => {
        if (!cancelled) setExistingReasons([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, trimmedCveOrRule, team, api]);

  return existingReasons;
}

/** "Copy details from" candidates — other active reasons on this same
 * (team, asset) that cover a *different* CVE/rule. Excludes reasons
 * matching this exact CVE — those already surface via the "attach to
 * existing reason" path, which links this finding to the original record
 * instead of cloning it. This is a pure prefill convenience: it still
 * creates a brand-new, independently-scoped `(team, cve_or_rule)` reason,
 * just pre-populated from an existing one on the same asset. */
export function useTemplateReasons(
  open: boolean,
  asset: string | undefined,
  team: string | undefined,
  trimmedCveOrRule: string,
  api: SecurityInsightsClient,
): QuiesceReason[] {
  const [templateReasons, setTemplateReasons] = useState<QuiesceReason[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !asset) {
      setTemplateReasons([]);
      return () => {
        cancelled = true;
      };
    }
    api
      .listQuiesceReasons({
        status: 'active',
        selectedTeam: team,
        selectedAsset: asset,
        exactAssetMatch: true,
      })
      .then(reasons => {
        if (!cancelled) {
          setTemplateReasons(
            reasons.filter(r => r.cve_or_rule !== trimmedCveOrRule),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setTemplateReasons([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, asset, team, trimmedCveOrRule, api]);

  return templateReasons;
}

/** The `cant_fix` reason-code taxonomy. `loaded` flips to `true` on both
 * success and failure (fail-closed) — callers use it to gate the "no
 * approved reason code" messaging so it doesn't flash true mid-fetch. */
export function useReasonCodes(
  open: boolean,
  api: SecurityInsightsClient,
): { reasonCodes: QuiesceReasonCode[]; reasonCodesLoaded: boolean } {
  const [reasonCodes, setReasonCodes] = useState<QuiesceReasonCode[]>([]);
  const [reasonCodesLoaded, setReasonCodesLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!open) {
      setReasonCodesLoaded(false);
      return () => {
        cancelled = true;
      };
    }
    api
      .getReasonCodes()
      .then(codes => {
        if (!cancelled) {
          setReasonCodes(codes.cant_fix);
          setReasonCodesLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReasonCodes([]);
          setReasonCodesLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, api]);

  return { reasonCodes, reasonCodesLoaded };
}
